import type { MentionPerson } from '../comments/commentMentions';
import type { RoomListItem, RoomMember } from './rooms';

/**
 * The people a members-only place may @: its members, matched against the
 * active TAP list rather than used as they come. The guard refuses an
 * inactive or non-TAP account anywhere (mention_must_be_active_tap_user), and
 * the active list carries the same label the picker shows everywhere else.
 * While the members are still loading this offers nobody rather than
 * everybody.
 */
export function membersOnlyPeople(
  members: RoomMember[] | undefined,
  everyone: MentionPerson[],
): MentionPerson[] {
  const memberIds = new Set((members ?? []).map((member) => member.userId));
  return everyone.filter((person) => memberIds.has(person.id));
}

/**
 * Who the @ picker offers in the open room.
 *
 * An all_tap channel (Team, Hub help) offers every active TAP person, as it
 * always has. A members-only room offers only its own members: every DM and
 * group (comment_mentions_guard refuses anyone else there, 20270180000100,
 * mention_must_be_room_member) and, from the drop after 3b-2, a members_only
 * channel such as the Agents room too (the same guard rule, 20270182000100).
 * createComment writes all of one message's mentions in a single insert
 * (comments/queries.ts), so one outsider in the list would cost the members
 * their mention too, with nothing on screen to say so. And in a private
 * channel an outsider's mention would tell them that the room exists.
 */
export function roomMentionPeople(
  room: RoomListItem | null,
  members: RoomMember[] | undefined,
  everyone: MentionPerson[],
): MentionPerson[] {
  if (room === null || !room.membersOnly) {
    return everyone;
  }
  return membersOnlyPeople(members, everyone);
}

/**
 * Where a draft is being written, for the refusal wording: anyone active on
 * the team, the people in this chat (a members-only room), or the people in
 * this project (an Alex Huddle reply, which is a task comment).
 */
export type MentionScope = 'everyone' | 'chat' | 'project';

export function roomMentionScope(room: RoomListItem | null): MentionScope {
  return room !== null && room.membersOnly ? 'chat' : 'everyone';
}

/**
 * The plain words shown when a draft names someone the place will not
 * accept (the message is not sent and the draft stays). Names come from the
 * pill's own label, so the person sees exactly who to take out.
 */
export function mentionRefusedMessage(scope: MentionScope, refused: { label: string }[]): string {
  const names = refused.map((person) => `@${person.label}`).join(', ');
  const where =
    scope === 'chat'
      ? 'Only people in this chat can be mentioned here.'
      : scope === 'project'
        ? 'Only people in this project can be mentioned here.'
        : 'Only active TAP people can be mentioned.';
  return `Not sent: ${names} cannot be mentioned here. ${where} Remove the mention and send again.`;
}

/**
 * The plain words shown when the message was posted but the database refused
 * its mentions (the batch is all or nothing), so nobody was notified.
 */
export function mentionNotDeliveredMessage(databaseMessage: string): string {
  const why =
    databaseMessage === 'mention_must_be_room_member'
      ? ' Someone you mentioned is no longer in this chat.'
      : databaseMessage === 'mention_must_be_active_tap_user'
        ? ' Someone you mentioned can no longer be mentioned.'
        : '';
  return `Your message was sent, but nobody you mentioned was notified.${why}`;
}
